import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// Verndale Customization: Ticket TGGMS-382
// Product Listing Page, added new fuctionality for Sign In box
const mapStateToProps = (state: ApplicationState) => {
    const { canAddToCart,canSeePrices } = getSettingsCollection(state).productSettings;
    return {
        userName: state.context.session.userName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        currentLocation: getLocation(state),
        canAddToCart,
        canSeePrices, // Verndale Customization: Ticket TGGOPT-1673
    };
};

export interface signInStyles {
    signInLink?: LinkPresentationProps;
    actionsGridItemPriceSignIn?: GridItemProps;
}
// end verndale customization

type Props = ProductCardSelections & HasHistory & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductListProductCardStyles extends signInStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    titleTypography?: TypographyPresentationProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    actionsGridItemPriceSignIn: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-items: center;
            display: inline;
            border: 1px solid;
            margin-bottom: auto !important;

            a {
                cursor: pointer;
                text-decoration: underline;
            }

            @media (max-width: 767px) {
                margin-bottom: 0 !important;
            }
        `,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
            white-space: break-spaces;
        `,
        ellipsis: true,
    },
    signInLink: {
        css: css`
            font-weight: 500;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    const { canAddToCart, signInUrl, currentUserIsGuest, currentLocation, history, canSeePrices } = props;
    const actionsGridItemStyle = (canAddToCart || canSeePrices) ? { ...styles.actionsGridItem } : { ...styles.actionsGridItemPriceSignIn };

    // Verndale Customization: Ticket TGGMS-382
    // Product Listing Page, added new fuctionality for Sign In box
    const onSignInHandler = (e: React.MouseEvent) => {
        if (!canAddToCart || currentUserIsGuest) {
            e.preventDefault();
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInUrl}`);
            } else {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            }
        }
    };

    const loginLink = (
        <>
            <Typography>{translate("Please")} </Typography>
            <Link {...styles.signInLink} onClick={onSignInHandler} href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}>
                {translate("Sign In")}
            </Link>
            <Typography> {translate("into your account to see price and availability.")}</Typography>
        </>
    );
    // end Verndale Customization

    return (
        <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <GridItem {...styles.leftColumnGridItem}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={props.showCompare}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                />
            </GridItem>
            <GridItem {...styles.rightColumnGridItem}>
                <GridContainer {...styles.rightColumnGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <ProductListProductInformation
                            showBrand={props.showBrand}
                            showTitle={props.showTitle}
                            showPartNumbers={props.showPartNumbers}
                            showAvailability={props.showAvailability}
                            showAttributes={props.showAttributes}
                            showImageBadges={props.showImageBadges}
                            maxImageBadges={props.maximumNumberOfImageBadges}
                            showTextBadges={props.showTextBadges}
                            maxTextBadges={props.maximumNumberOfTextBadges}
                        />
                    </GridItem>
                    <GridItem {...actionsGridItemStyle}>
                        {(canAddToCart || canSeePrices) && ( // Verndale Customization: Ticket TGGOPT-1673
                            <ProductListActions showPrice={props.showPrice} showAddToList={props.showAddToList} />
                        )}
                        {!canAddToCart && !canSeePrices && loginLink} {/* Verndale Customization: Ticket TGGOPT-1673 */}
                    </GridItem>
                    <GridItem {...styles.searchScoreDataGridItem}>
                        <ProductSearchScoreData />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withHistory(withProduct(ProductListProductCard)));
